import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { api, ApiError } from "../api";
import { useBackdropDismiss } from "./Modal";

export default function ChangePasswordDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setDone(false);
  };

  const change = useMutation({
    mutationFn: () => api.changePassword(current, next),
    onSuccess: () => {
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    },
  });

  const dismiss = useBackdropDismiss(() => {
    reset();
    onClose();
  }, open);

  if (!open) return null;

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 8;
  const canSubmit =
    current.length > 0 && next.length >= 8 && next === confirm && !change.isPending;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      {...dismiss}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) change.mutate();
        }}
        className="bf-card w-full max-w-sm space-y-4"
      >
        <div className="flex items-center gap-2">
          <KeyRound size={18} className="text-bf-muted" />
          <h2 className="text-base font-medium">Change password</h2>
        </div>

        {done ? (
          <>
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-emerald-300">
              Password updated.
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="bf-button"
                onClick={() => {
                  reset();
                  onClose();
                }}
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">
                Current password
              </span>
              <input
                type="password"
                className="bf-input"
                value={current}
                autoFocus
                autoComplete="current-password"
                onChange={(e) => setCurrent(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">
                New password
              </span>
              <input
                type="password"
                className="bf-input"
                value={next}
                autoComplete="new-password"
                onChange={(e) => setNext(e.target.value)}
              />
              {tooShort && (
                <span className="text-[10px] text-amber-300">
                  Use at least 8 characters.
                </span>
              )}
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">
                Confirm new password
              </span>
              <input
                type="password"
                className="bf-input"
                value={confirm}
                autoComplete="new-password"
                onChange={(e) => setConfirm(e.target.value)}
              />
              {mismatch && (
                <span className="text-[10px] text-amber-300">
                  Passwords do not match.
                </span>
              )}
            </label>

            {change.isError && (
              <div className="text-sm text-red-400">
                {change.error instanceof ApiError
                  ? change.error.message
                  : "Could not change password"}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="bf-button bg-white/10 bg-none"
                onClick={() => {
                  reset();
                  onClose();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bf-button disabled:opacity-40"
                disabled={!canSubmit}
              >
                {change.isPending ? "Saving…" : "Update password"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
